// services/sensor.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, map, takeUntil } from 'rxjs';
import { Sensor, SensorDataSummary, DataPoint } from '../models/sensor';
import { environment } from '../../../environments/environment';
import { RequestSensor } from '../models/request-sensor';
import { Threshold } from '../models/threshold';
import { PonctualIndicator, WFSSensor } from '../models/ponctual-indicator';
import { SymbologyService } from './symbology.service';
import { IPSData } from '../models/ips-data';
import { LatestDepth } from '../models/latest-depth';
import { HydroHubeau } from '../models/hydro-hubeau';
import { DataDepth } from '../models/depth-data';
import { isSameDay } from 'date-fns';

@Injectable({
  providedIn: 'root'
})
export class SensorService {
  private apiUrl = environment.apiUrl;
  private sensorsSubject: BehaviorSubject<PonctualIndicator[]> = new BehaviorSubject<PonctualIndicator[]>([]);
  private cancelRequests$ = new Subject<void>();
  private cancelFetchPiezoSensor$ = new Subject<void>();

  constructor(private http: HttpClient, private symbologyService: SymbologyService) { }

  cancelRequests() {
    this.cancelRequests$.next();
  }

  cancelFetchPiezoSensorRequest() {
    this.cancelFetchPiezoSensor$.next();
    this.cancelFetchPiezoSensor$ = new Subject<void>();
  }

  getSensors(sensorType: string): Observable<Sensor[]> {
    return this.http.get<Sensor[]>(`${this.apiUrl}/Capteur/${sensorType}`);
  }

  getSensorsHubeauPiezo(): Observable<any> {
    let seuil_mesures_piezo_min = 650; // seuil de 650 mesures minimum pour prendre en compte les piezometres
    return this.http.get<Sensor[]>(`https://hubeau.eaufrance.fr/api/v1/niveaux_nappes/stations?fields=code_bss%2Cbss_id%2Cx%2Cy%2Clibelle_pe&format=json&nb_mesures_piezo_min=${seuil_mesures_piezo_min}&size=20000`);
  }

  getSensorHubeauDebit(): Observable<any> {
    return this.http.get<Sensor[]>(`https://hubeau.eaufrance.fr/api/v2/hydrometrie/referentiel/stations?fields=code_station%2Clibelle_station%2Clongitude_station%2Clatitude_station&format=json&size=10000`);
  }

  downloadDataSensor(sensorType: string, identifier: string): Observable<string> {
    const headers = new HttpHeaders({
      'Accept': 'text/plain',
      'Content-Type': 'text/plain',
    });

    return this.http.get(`${this.apiUrl}/Capteur/${sensorType}/${encodeURIComponent(identifier)}/data.csv`, { headers, responseType: 'text' })
      .pipe(
        map(response => response as string)
      );
  }

  addSensor(sensor: RequestSensor): Observable<Sensor> {
    return this.http.put<Sensor>(`${this.apiUrl}/Capteur`, sensor);
  }

  updateSensor(sensor: RequestSensor): Observable<Sensor> {
    return this.http.post<Sensor>(`${this.apiUrl}/Capteur`, sensor);
  }

  getIpsDate(sensorType: string, identifier: string): Observable<Sensor> {
    return this.http.get<Sensor>(`${this.apiUrl}/Capteur/${sensorType}/${encodeURIComponent(identifier)}/ipsDate`);
  }

  updateIpsDate(sensorType: string, identifier: string, date: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/Capteur/${sensorType}/${encodeURIComponent(identifier)}/ipsDate?date=${date}`, {});
  }

  postDataFile(identifier: string, sensorType: string, content: string): Observable<any> {
    const formData = new FormData();
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    formData.append('data', blob, 'data.csv');
    return this.http.post(`${this.apiUrl}/Capteur/${sensorType}/${encodeURIComponent(identifier)}/data.csv`, formData);
  }


  getThresholds(sensorType: string, identifier: string): Observable<Threshold[]> {
    return this.http.get<Threshold[]>(`${this.apiUrl}/Capteur/${sensorType}/${encodeURIComponent(identifier)}/seuils`);
  }

  updateThresholds(sensorType: string, identifier: string, thresholds: Threshold[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/Capteur/${sensorType}/${encodeURIComponent(identifier)}/seuils`, thresholds);
  }

  getSensorDataSummary(type: string, identifier: string): Observable<SensorDataSummary> {
    return this.http.get<SensorDataSummary>(`${this.apiUrl}/Capteur/${encodeURIComponent(type)}/${encodeURIComponent(identifier)}/data/summary`);
  }

  getIpsData(identifier: string): Observable<IPSData> {
    return this.http.get<IPSData>(`${this.apiUrl}/Capteur/IPS/${encodeURIComponent(identifier)}`);
  }

  getSensorLatestDepth(identifier: string, type: string, depth: boolean): Observable<LatestDepth> {
    return this.http.get<any[]>(`${this.apiUrl}/Capteur/${type}/${encodeURIComponent(identifier)}/data?depth=${depth}&realtime=true`).pipe(
      map((dataArray) => {
        const lastDate = (dataArray[0].length > 0) ? dataArray[0].at(-1) : {date:null,value:0};
        const firstDate = (dataArray.length > 1 && dataArray[1].length > 0) ? dataArray[1][0] : {date:null,value:0};
        if (isSameDay(lastDate.date,firstDate.date)) {
          dataArray[1].shift(); //enleve la premiere date du temps reel si identique aux donnees
        }
        const concatArray = [...dataArray[0], ... dataArray[1]];
        return this.getMostRecentData(concatArray);
      })
    );
  }

  // Fonction pour extraire l'objet avec la date la plus récente
  private getMostRecentData(dataArray: any[]): any {
    if (dataArray && dataArray.length > 0) {
      return dataArray.reduce((prev, current) => {
        return new Date(prev.date) > new Date(current.date) ? prev : current;
      });
    }
    return null; // Si le tableau est vide, retourner null ou gérer autrement
  }

  getSensorDataRealtime(type: string, identifier: string, param: string): Observable<DataPoint[]> {
    return this.http.get<DataPoint[]>(`${this.apiUrl}/Capteur/${type}/${encodeURIComponent(identifier)}/data?${param}&realtime=true`);
  }

  getSensorDataDepth(type: string, identifier: string, param: string, depth: boolean): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/Capteur/${type}/${encodeURIComponent(identifier)}/data?${param}&depth=${depth}&realtime=true`);
  }

  fetchPiezoSensorForMap(param: string): Observable<IPSData[]> {
    return this.http.get<IPSData[]>(`${this.apiUrl}/Capteur/IPS?${param}`)
      .pipe(takeUntil(this.cancelFetchPiezoSensor$));
  }

  fetchSensorForMap(type: string, param: string): Observable<Sensor[]> {
    return this.http.get<Sensor[]>(`${this.apiUrl}/Capteur/${type}?${param}`).pipe(takeUntil(this.cancelRequests$));;
  }

  fetchHydroSensorHubeau(bbox: string): Observable<HydroHubeau> {
    return this.http.get<HydroHubeau>(
      environment.hubeauBase + `/v2/hydrometrie/referentiel/stations?en_service=true&fields=libelle_station,code_station,coordonnee_x_station,coordonnee_y_station&format=json&bbox=${bbox}`
    ).pipe(takeUntil(this.cancelRequests$));
  }

  getSensorsByUltimatum(): Observable<PonctualIndicator[]> {
    return this.sensorsSubject.asObservable();
  }

  getSensorSeuils(type: string, codeBSS: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/Capteur/${type}/${encodeURIComponent(codeBSS)}/seuils`);
  }


}